// ─── NOTES ────────────────────────────────────────────────────────────
//
// 1. FRAME_PIVOT MORPH
//    frame_pivot moves post positions: 0 = max spread, 1 = min (1m × 1m)
//    It moves posts in BOTH X and Z simultaneously.
//    The formula uses average of width+length morph values, inverted.
//    This needs testing — if it doesn't look right, try:
//      - Using just targetWidth (if pivot only affects X)
//      - Using max() instead of average
//      - Different inversion formula
//
// 2. ROOF TYPE SWITCHING
//    When user switches roof type:
//      - Clamp dimensions to new roof type limits using getDimensionLimits()
//      - Update slider min/max in UI
//      - Reset louvreType to 0
//      - Call changeDimensions() to apply correct morph set
//      - Switch frame meshes (frame_pivot_x/y ↔ frame_retr_2_x/y)
//
// 4. POST SIZE SWITCHING
//    Old model used "6-8" morph for post size.
//    New model: switching post size means switching entire mesh templates
//    (post_100_retr_* ↔ post_150_retr_*) via switchPostSize().
//    The morph "6-8" call has been removed.
//
// 5. HEIGHT OFFSETS FOR ZIP
//    Old values were for feet (9, 10, 11, 12 feet).
//    New values need calibration for meters (2.7 - 3.5m range).
//    Test and adjust the thresholds.
//
// ─────────────────────────────────────────────────────────────────────


export const frameConfig = {
    // Opening Roof (roofType 0) — single frame pair
    opening: {
        x: "frame_pivot_x",
        y: "frame_pivot_y",
    },
    // Retractable Roof (roofType 1) — default frame (type 2)
    retractable_default: {
        x: "frame_retr_2_x",
        y: "frame_retr_2_y",
    },
    // Retractable Roof — partial frame (type 1, used when louvers removed from one side)
    retractable_partial: {
        x_B: "frame_retr_1_x_B",
        x_F: "frame_retr_1_x_F",
        y_L: "frame_retr_1_y_L",
        y_R: "frame_retr_1_y_R",
    },
};

// ─── POSTS ───────────────────────────────────────────────────────────

export const postConfig = {
    100: {
        FR: "post_100_retr_FR",
        FL: "post_100_retr_FL",
        BL: "post_100_retr_BL",
        BR: "post_100_retr_BR",
        FC: "post_100_retr_FC",
        BC: "post_100_retr_BC",
        C_x: "post_100_retr_C_x",
        C_y: "post_100_retr_C_y",
    },
    150: {
        FR: "post_150_retr_FR",
        FL: "post_150_retr_FL",
        BL: "post_150_retr_BL",
        BR: "post_150_retr_BR",
        FC: "post_150_retr_FC",
        BC: "post_150_retr_BC",
        C_x: "post_150_retr_C_x",
        C_y: "post_150_retr_C_y",
    },
};

export const postSizeMap = {
    0: 100,
    1: 150,
};


export const postSideMap = {
    frontCenter: "FC",
    backCenter: "BC",
    leftCenter: "FL",
    rightCenter: "FR",
    centerCenter: null, // dynamically based on directionRoof → C_x or C_y
};

// ─── BEAMS ───────────────────────────────────────────────────────────

export const beamConfig = {
    // Same beams for both roof types
    x: "mid_beam_retr_x",
    y: "mid_beam_retr_y",
};

// ─── LOUVERS ─────────────────────────────────────────────────────────

export const louverConfig = {
    // Opening Roof (roofType 0) — 4 types
    opening: {
        0: { x: "180/30_Slimline_Roof_x", y: "180/30_Slimline_Roof_y", label: "180/30 Slimline Roof" },
        1: { x: "200/35_Slimline_Roof_x", y: "200/35_Slimline_Roof_y", label: "200/35 Slimline Roof" },
        2: { x: "200_Suburban_Roof_x", y: "200_Suburban_Roof_y", label: "200 Suburban Roof" },
        3: { x: "220/35_Slimline_Roof_x", y: "220/35_Slimline_Roof_y", label: "220/35 Slimline Roof" },
        4: { x: "220/45_Alpine_Roof_x", y: "220/45_Alpine_Roof_y", label: "220/45 Alpine Roof" },
        5: { x: "270_Translucent_Roof_x", y: "270_Translucent_Roof_y", label: "270 Translucent Roof" },
    },
    // Retractable Roof (roofType 1) — 2 types
    retractable: {
        0: { x: "220/35_Slimline_Retract_x", y: "220/35_Slimline_Retract_y", label: "220/35 Slimline Retract" },
        1: { x: "220/45_Alpine_Retract_x", y: "220/45_Alpine_Retract_y", label: "220/45 Alpine Retract" },
    },
};

// ─── HEATERS ─────────────────────────────────────────────────────────

export const heaterConfig = {
    round: "heater_round",
    horizontal_black: "horizontal_heater_black",
    horizontal_white: "horizontal_heater_white",
};

// ─── LIGHTS ──────────────────────────────────────────────────────────

export const lightConfig = {
    conduit_x: "light_conduit_x",
    conduit_y: "light_conduit_y",
    spot: "light_spot",
};

// ─── MORPH TARGETS ───────────────────────────────────────────────────

/**
 * Morph name mapping by roof type.
 *
 * Posts have roof-type-specific morphs:
 *   - Opening Roof: frame_pivot, frame_pivot_x_width, etc.
 *   - Retractable:  frame_retr_x_width, etc.
 *
 * Frames and beams use simple: width, lenth, height
 *
 * All meshes share: height
 */
export const morphConfig = {
    // Posts — roof-type-dependent morphs
    posts: {
        opening: {
            // frame_pivot: 0 = max size, 1 = min size (1m × 1m)
            // Moves post position along x and y simultaneously
            position: "frame_pivot",
            // Direction-specific dimension morphs
            x_width: "frame_pivot_x_width",
            x_length: "frame_pivot_x_lenth",
            y_width: "frame_pivot_y_width",
            y_length: "frame_pivot_y_lenth",
            height: "height",
        },
        retractable: {
            x_width: "frame_retr_x_width",
            x_length: "frame_retr_x_lenth",
            y_width: "frame_retr_y_width",
            y_length: "frame_retr_y_lenth",
            height: "height",
        },
    },

    // Frames — simple width/lenth/height (same names for both roof types)
    frames: {
        width: "width",
        length: "lenth",
        height: "height",
    },

    // Beams — width + height only (stretch along one axis, positioned by code)
    beams: {
        width: "width",
        height: "height",
    },

    // Louvers — stretch along one axis (x or y variant handles the other)
    louvers: {
        width: "width",
        length: "lenth",
    },
};

// ─── HELPERS ─────────────────────────────────────────────────────────

/**
 * Get louver types available for current roof type.
 * @param {number} roofType - 0 = Opening, 1 = Retractable
 * @returns {object} Map of louverType index → { x, y, label }
 */
export function getLouverTypes(roofType) {
    return roofType === 0 ? louverConfig.opening : louverConfig.retractable;
}

/**
 * Get the louver mesh names for current state.
 * @param {number} roofType
 * @param {number} louverType - index within the roof type's louver set
 * @returns {{ x: string, y: string }}
 */
export const louverMeshNames = {
    opening: {
        0: { x: "18030_Slimline_Roof_x", y: "18030_Slimline_Roof_y" },
        1: { x: "20035_Slimline_Roof_x", y: "20035_Slimline_Roof_y" },
        2: { x: "200_Suburban_Roof_x", y: "200_Suburban_Roof_y" },
        3: { x: "22035_Slimline_Roof_x", y: "22035_Slimline_Roof_y" },
        4: { x: "22045_Alpine_Roof_x", y: "22045_Alpine_Roof_y" },
        5: { x: "270_Translucent_Roof_x", y: "270_Translucent_Roof_y" }
    },
    retractable: {
        0: { x: "22035_Slimline_Retract_Roof_x", y: "22035_Slimline_Retract_Roof_y" },
        1: { x: "22045_Alpine_Retract_Roof_x", y: "22045_Alpine_Retract_Roof_y" },
    }
};

export function getLouverMeshNames(roofType, louvreType, axis) {
    const group = roofType === 0 ? louverMeshNames.opening : louverMeshNames.retractable;
    return group[louvreType]?.[axis] || null;
}

/**
 * Get the frame mesh names for current state.
 * @param {number} roofType - 0 = Opening, 1 = Retractable
 * @returns {{ x: string, y: string }}
 */
export function getFrameMeshNames(roofType) {
    return roofType === 0 ? frameConfig.opening : frameConfig.retractable_default;
}

/**
 * Get the post mesh name for a given size and position.
 * @param {number} postSizeIndex - state.postSize (0 = 100, 1 = 150)
 * @param {string} position - "FR", "FL", "BL", "BR", "FC", "BC", "C_x", "C_y"
 * @returns {string} Mesh name
 */
export function getPostMeshName(postSizeIndex, position) {
    const size = postSizeMap[postSizeIndex] || 100;
    return postConfig[size]?.[position] || "";
}

/**
 * Get the morph name set for posts based on roof type.
 * @param {number} roofType
 * @returns {object}
 */
export function getPostMorphSet(roofType) {
    return roofType === 0 ? morphConfig.posts.opening : morphConfig.posts.retractable;
}

/**
 * Get center post mesh name based on direction.
 * @param {number} postSizeIndex
 * @param {number} directionRoof - 0 or 1
 * @returns {string}
 */
export function getCenterPostMeshName(postSizeIndex, directionRoof) {
    const position = directionRoof ? "C_y" : "C_x";
    return getPostMeshName(postSizeIndex, position);
}